"use client"

/**
 * Centred-modal Dialog primitive — wraps @radix-ui/react-dialog.
 *
 * Radix gives us, out of the box:
 *   - role="dialog" + aria-modal="true"
 *   - Focus trap inside the dialog while open
 *   - Initial focus on the first focusable child
 *   - Return focus to the trigger element on close
 *   - Esc-to-close + click-outside-to-close
 *   - Body scroll lock + inert background
 *
 * Companion to `sheet.tsx` (side drawer); both wrap the same Radix
 * package. Use Dialog for confirmations, forms, anything that should
 * sit in the centre of the viewport. Use Sheet when the content is
 * spatially related to the side it slides from.
 */

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { useVisualViewport } from "@/hooks/useVisualViewport"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /** Hide the built-in top-end close button. */
  hideClose?: boolean
  /** Custom max-width class (defaults to a sensible md). */
  maxWidthClassName?: string
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, hideClose, maxWidthClassName, ...props }, ref) => {
  // Centre on the *visible* area, not the layout viewport. With a
  // keyboard up on iOS the two diverge by the keyboard's height, and a
  // `top: 50%` dialog puts its own middle — the inputs and the submit
  // button — behind the keyboard with no way to scroll to them.
  const { height: viewportHeight, offsetTop, keyboardOpen } =
    useVisualViewport()

  return (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed start-1/2 z-50 grid w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl shadow-2xl outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
        // Fallback for browsers without visualViewport — identical to
        // the previous fixed positioning.
        viewportHeight ? undefined : "top-1/2 max-h-[90vh]",
        maxWidthClassName ?? "max-w-md",
        className,
      )}
      style={{
        backgroundColor: "var(--color-bg-primary)",
        color: "var(--color-text-primary)",
        ...(viewportHeight
          ? {
              top: offsetTop + viewportHeight / 2,
              // Give the content a touch more room once the keyboard
              // has already taken most of the screen.
              maxHeight: viewportHeight * (keyboardOpen ? 0.96 : 0.9),
            }
          : {}),
      }}
      {...props}
    >
      {children}
      {!hideClose && (
        <DialogPrimitive.Close
          aria-label="Close dialog"
          className="absolute end-3 top-3 inline-flex items-center justify-center min-h-11 min-w-11 rounded-lg opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)] focus-visible:ring-offset-2 disabled:pointer-events-none"
          style={{ color: "var(--color-text-secondary)" }}
        >
          <X className="h-5 w-5" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-start px-6 pt-6 pb-2",
      className,
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end gap-2 px-6 pb-6 pt-2",
      className,
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold tracking-tight leading-none",
      className,
    )}
    style={{ color: "var(--color-text-primary)" }}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm", className)}
    style={{ color: "var(--color-text-secondary)" }}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

/**
 * DialogBody — convenience wrapper for content between header + footer.
 * Just a div with consistent padding; not a Radix primitive.
 */
const DialogBody = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("px-6 py-4", className)} {...props} />
)
DialogBody.displayName = "DialogBody"

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogBody,
  DialogTitle,
  DialogDescription,
}
